import theme from "src/theme/light";
import { Box, Button, Paper, Tooltip, Typography, useMediaQuery } from "@mui/material"
import { Alert } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { TIPO_SERVICO, TIPO_SERVICO_REVENDA } from "src/components/projetos/constants";
import { GrupoEstrutura } from "src/components/projetos/types";
import { FullWidthButton } from "src/framework/components/buttons/FullWidthButton";
import { BottomBar } from "src/framework/components/commons/BottomBar";
import { useObservable } from "src/framework/store/storeUtil";
import { grupoEstrutura$, limitacoes$ } from "src/store/global.store";
import { formState$ } from "src/store/vendas/projetos/store";
import { PageTitle } from "../containers/PageTitle";
import { ServicoCodeModal } from "../../containers/ServicoCodeModal";
import { notifierStore } from "src/framework/store/NotifierStore";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";
import { InputDecimal3 } from "src/framework/components/forms/inputs/InputDecimal3";
import { InputText } from "src/framework/components/forms/inputs/InputText";

interface InformacoesProjetoProps {
  formState: any;
  onChangeValue: (formState: any) => void
  errorState: string[];
  onChangeError: (errorState: any) => void
  standAlone?: boolean;
  nextTabIsDisabled?: { status: boolean, tooltipMessage?: string };
  readOnly?: boolean;
  onClickNext: () => void
  onClickBack: () => void
}

const DEFAULT_SERVICO = { text: "Produto", value: "" }

const VALOR_SERVICO = "valor_servico";

const InformacoesProjeto: FC<InformacoesProjetoProps> = ({ onClickBack, nextTabIsDisabled, onClickNext, standAlone, readOnly, formState, onChangeValue, errorState, onChangeError }) => {
  const grupoEstruturaState = useObservable(grupoEstrutura$);
  const limitacoesProjetoState = useObservable(limitacoes$);

  const [tipoServico, setTipoServico] = useState(DEFAULT_SERVICO);
  const [solicitacaoState, setSolicitacaoState] = useState<any>(null)

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterButton = isMobile ? Box : BottomBar;

  const TIPOS_SERVICO_CUSTOM = useMemo(() => {
    return TIPO_SERVICO.map(item => {
      if (item.value === TIPO_SERVICO_REVENDA) return { ...item, disabled: !formState?.revenda?.segmentos_servico?.includes(formState?.segmento?.includes("on") ? "on" : "off")}
      return item;
    })
  }, [formState?.revenda?.segmentos_servico, formState?.segmento])

  useEffect(()=>{
    if (formState?.servico) {
      const servico = TIPOS_SERVICO_CUSTOM.find(item => item.value === formState.servico) || DEFAULT_SERVICO
      setTipoServico(servico)
    }
  },[formState, onChangeValue, TIPOS_SERVICO_CUSTOM])

  const servicoLabel = useMemo(()=>{
    return TIPOS_SERVICO_CUSTOM.find(item => item.value === TIPO_SERVICO_REVENDA)?.text || ""
  }, [TIPOS_SERVICO_CUSTOM])

  const parseLimitacaoSistema = useCallback(() => {
    if (limitacoesProjetoState?.data) {
      const index = limitacoesProjetoState.data.findIndex((l: any) => {
          return l.id === formState?.solicitacao?.limitacao_projeto_id
      });
      if (index > -1) {
          return limitacoesProjetoState.data[index];
      }
    }
    return undefined
  }, [formState, limitacoesProjetoState])

  useEffect(() => {
    const _solicitacao = formState?.solicitacao?.limitacao_projeto_id === null ? null : parseLimitacaoSistema();
    if (_solicitacao && Object.keys(_solicitacao).length > 0) {
      setSolicitacaoState({ ..._solicitacao });
    }
  }, [formState?.solicitacao, parseLimitacaoSistema])

  const tipoFaturamentoOptions = useMemo(() => {
    if (formState?.recebe_servico && formState?.revenda_id) {
      return TIPOS_SERVICO_CUSTOM;
    }
    
    return TIPOS_SERVICO_CUSTOM.filter(item => item.value !== TIPO_SERVICO_REVENDA)
  }, [TIPOS_SERVICO_CUSTOM, formState?.recebe_servico, formState?.revenda_id])

  useEffect(()=>{
    const tipoServicoRevenda = tipoFaturamentoOptions.find(item => item.value === TIPO_SERVICO_REVENDA && !item.disabled);

    if (tipoServico?.value === TIPO_SERVICO_REVENDA && Number(formState?.valor_servico || 0) > 0 && !tipoServicoRevenda) {
      const state: any = { ...formState };
      state.servico = "";
      onChangeValue(state);
      setTipoServico(DEFAULT_SERVICO)
    }
  },[formState, onChangeValue, tipoFaturamentoOptions, tipoServico?.value])

  const validate = (): boolean => {
    const messages: string[] = [];
    const servicoRevenda = formState?.servico === TIPO_SERVICO_REVENDA;
    const valorServico = !!(formState?.valor_servico && Number(formState.valor_servico) > 0);

    if (servicoRevenda && !valorServico) {
      messages.push("Valor de serviço deve ser maior que zero");
    }
    if (messages.length > 0) {
      notifierStore.setNotifier({
        title: "Aviso",
        content: messages,
        timeOut: 3500,
        onClose: () => {
          notifierStore.setOpen(false)
        }
      })
      notifierStore.setOpen(true)
      return false;
    }
    return messages.length === 0;
  }

  return (
    <>
      <Paper elevation={0}>
        <PageTitle title="Projeto Especial" codigoVenda={formState?.codigo_venda} />
        <Box p={2}>
          <Box className="X12" py={2}>
            <Typography variant="h6" align="left"><strong>Informações do projeto:</strong></Typography>
          </Box>

          {!!formState && (!formState?.recebe_servico && !formState?.engenharia) && (
            <Alert severity="warning">
              Estamos temporariamente com a proposta <strong>{servicoLabel}</strong> suspensa.
            </Alert>
          )}

          <div className="row">
            <div className="X12 L6">
              <InputComboBoxList
                multiple
                limitTags={3}
                id={`listagem-verticais-grupo-estrutura-cb`}
                key={`listagem-verticais-grupo-estrutura-cb`}
                label={"Estrutura"}
                textField="nome"
                valueField="id"
                options={grupoEstruturaState?.data?.filter((d: any) => d.vertical === true) || []}
                value={formState?.disposicoes?.filter((d: GrupoEstrutura) => !Object.keys(d).includes("@remove")) || []}
                autoComplete
                onChange={(value: GrupoEstrutura[]) => {
                    const state: any = { ...formState };
                    state.disposicoes = value;
                    onChangeValue(state)
                }}
                renderOptions={(option: any) => {
                    return `${option.nome}`
                }}
                renderOptionsLabel={(option: any) => {
                    return `${option.nome}`
                }}
              />
            </div>

            <div className="X12 L6">
              <InputComboBoxList
                id={`listagem-limitacoes-${formState?.id}`}
                key={`listagem-limitacoes`}
                label={"Limitações do projeto"}
                textField="descricao"
                valueField="id"
                options={limitacoesProjetoState?.data}
                value={solicitacaoState}
                autoComplete
                onChange={(value: any) => {
                    const state: any = { ...formState }
                    if (value) {
                        state.solicitacao = { ...state.solicitacao, limitacao_projeto_id: value?.id }
                        setSolicitacaoState(state.solicitacao);
                    } else {
                        state.solicitacao = { ...state.solicitacao, limitacao_projeto_id: null }
                        setSolicitacaoState(undefined);
                    }
                    onChangeValue(state)
                }}
                renderOptions={(option: any) => {
                    return `${option?.descricao}`
                }}
                renderOptionsLabel={(option: any) => {
                    if (option) {
                        return `${option?.descricao}`
                    }
                    return "";
                }}
              />
            </div>

            <div className="X12">
              <InputComboBoxList
                disableClearable
                label="* Tipo de faturamento"
                error={errorState.includes(VALOR_SERVICO)}
                options={tipoFaturamentoOptions}
                disabled={formState?.codigo_venda === ""}
                value={tipoServico}
                textField="text"
                valueField="value"
                onChange={(value: any) => {
                    const state: any = { ...formState }
                    state.servico = !value ? "" : value.value
                    onChangeValue(state);
                    setTipoServico(value ? value : DEFAULT_SERVICO)
                }}
              />
            </div>

            {formState?.servico === TIPO_SERVICO_REVENDA && (
              <>
                <div className="X12 M12">
                  <InputDecimal3
                    allowNegative={false}
                    InputLabelProps={{ shrink: true }}
                    label="Valor do serviço líquido (R$)"
                    disabled={formState?.servico !== TIPO_SERVICO_REVENDA}
                    error={errorState.includes(VALOR_SERVICO)}
                    margin="normal"
                    decimal={2}
                    maxLength={15}
                    prefix={<>R$&nbsp;</>}
                    value={String(formState?.valor_servico || 0)}
                    onChange={(event: any) => {
                        const state: any = { ...formState };
                        state.valor_servico = event.target.value
                        onChangeValue(state);
                    }}
                    onKeyUp={(e: any) => {
                        if (e.target.value) {
                            const idxError = errorState.findIndex((fs) => fs === VALOR_SERVICO)
                            if (idxError > -1) {
                                errorState.splice(idxError, 1);
                                onChangeError([...errorState])
                            }
                        }
                    }}
                  />
                </div>
              </>
            )}
          </div>

          <InputText label="* Observações" multiline maxRows={7} minRows={7}
            InputProps={{
                rows: 7
            }}
            fullWidth
            helperText="As observações devem ter no mínimo 20 caracteres para prosseguir."
            value={formState?.solicitacao?.limitacao_observacao}
            onChange={(e: any) => {
              const state: any = { ...formState }
              state.solicitacao = { ...state.solicitacao, limitacao_observacao: e.target.value }
              onChangeValue(state)
            }}
          />
        </Box>
      </Paper>

      <FooterButton style={isMobile ? { marginTop: 10, padding: 15 } : {}}>
        {standAlone ? "" : (
          <Button
            color={!readOnly ? "secondary" : "primary"}
            onClick={() => {
              formState$.next({...formState$.value, ...formState})
              onClickBack()
            }}
            variant='outlined'
            fullWidth={isMobile}
            style={isMobile ? { marginTop: 15 } : {}}
          >
            {!readOnly ? "Voltar" : "Fechar"}
          </Button>
        )}
        {!readOnly ?
          <Tooltip title={nextTabIsDisabled?.status ? (nextTabIsDisabled.tooltipMessage || "") : ''}>
            <span>
              <FullWidthButton
                disableElevation={true}
                text={"Continuar"}
                disabled={nextTabIsDisabled?.status}
                color={"primary"}
                variant={"contained"}
                onClick={() => {
                  if (!validate()) {
                    return
                  }
                  
                  formState$.next({...formState$.value, ...formState})
                  onClickNext()
                }}
                style={isMobile ? { marginTop: 15, marginBottom: 15 } : {}}
              />
            </span>
          </Tooltip>
        : ""}
      </FooterButton>
      <ServicoCodeModal />
    </>
  )
}

export { InformacoesProjeto }